import { Button } from '~/components/ui/button'
import { useIntercom } from 'react-use-intercom'
import { useState, useEffect } from 'react'
import { useLoaderData, useActionData, Form, LoaderFunctionArgs, ActionFunctionArgs } from 'react-router'
import { trackIntercomLead } from '~/lib/intercom/trackIntercomLead.server'
import Footer from '~/components/Footer'
import Navigation from '~/components/Navigation'
import { HEJA_STATS } from '~/config/stats'
import { useAnalytics } from '~/contexts/analytics/AnalyticsProvider'
import { getLang } from '~/lib/getLang'
import { logEvent } from '~/lib/events'
import useTranslator from '~/lib/useTranslator'
import { mergeMeta } from '~/utils/mergeMeta'

// Declare Facebook pixel types
declare global {
  interface Window {
    fbq?: (action: string, event: string, params?: Record<string, any>) => void
  }
}

export const meta = mergeMeta(() => [
  { title: 'Heja | Fundraising' },
  {
    name: 'description',
    content: 'Raise funds digitally with your team on Heja. No fees, minimums, or admin work.',
  },
  { property: 'og:title', content: 'Heja | Fundraising' },
  {
    property: 'og:description',
    content: 'Raise funds digitally with your team on Heja. No fees, minimums, or admin work.',
  },
])

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const

export async function loader({ request }: LoaderFunctionArgs) {
  const lang = getLang({ request })
  return { lang }
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()
  const email = formData.get('email') as string
  const cause = formData.get('cause') as string
  const goal = formData.get('goal') as string

  if (!email) {
    return { ok: false, error: 'Email is required' }
  }

  const url = new URL(request.url)
  const utm: Record<string, string> = {}
  for (const key of UTM_KEYS) {
    const value = url.searchParams.get(key)
    if (value) utm[key] = value
  }

  const result = await trackIntercomLead({
    email,
    eventName: 'fundraising-form-submitted',
    metadata: {
      cause: cause || undefined,
      goal: goal || undefined,
      lead_source: 'fundraising_page',
      ...utm,
    },
  })

  return { ok: result.ok }
}

export default function Fundraising() {
  const { lang } = useLoaderData<typeof loader>()
  const actionData = useActionData<typeof action>()
  const Translator = useTranslator()
  const intercom = useIntercom()
  const { trackEvent } = useAnalytics()
  const [showForm, setShowForm] = useState(false)
  const [showGuide, setShowGuide] = useState(false)
  const [formData, setFormData] = useState({
    email: '',
    cause: '',
    goal: '',
  })

  useEffect(() => {
    if (actionData?.ok) {
      setShowForm(false)
      setShowGuide(true)
    }
  }, [actionData])

  // Helper function for Meta pixel events
  const trackMetaPixel = (event: string, params?: Record<string, any>) => {
    if (typeof window !== 'undefined' && window.fbq) {
      window.fbq('track', event, params)
    }
  }

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    const form = new FormData(e.currentTarget)

    // Track Meta pixel conversion event directly
    trackMetaPixel('Lead', {
      content_name: 'fundraising_form_submit',
      content_category: 'fundraising',
      value: form.get('goal') as string,
      currency: 'USD',
    })

    // Track Meta conversion event via GTM (backup)
    logEvent('Lead', {
      content_name: 'fundraising_form_submit',
      content_category: 'fundraising',
      value: form.get('goal') as string,
      currency: 'USD',
    })

    // Track analytics event
    trackEvent('fundraising_form_submitted', {
      cause: (form.get('cause') as string) || '',
      goal: (form.get('goal') as string) || '',
    })

    // Intercom lead is tracked server-side in the route action (with UTM params)
  }

  const handleGetStarted = () => {
    // Track Meta pixel conversion event directly
    trackMetaPixel('Lead', {
      content_name: 'fundraising_get_started',
      content_category: 'fundraising',
    })

    // Track Meta conversion event via GTM (backup)
    logEvent('Lead', {
      content_name: 'fundraising_get_started',
      content_category: 'fundraising',
    })

    // Track analytics event
    trackEvent('fundraising_get_started_clicked')

    // Show form and scroll to top without animation
    setShowForm(true)
    window.scrollTo(0, 0)
  }

  if (showGuide) {
    return (
      <>
        <Navigation />
        <main className="bg-white min-h-screen py-12">
          <div className="max-w-5xl mx-auto px-6">
            {/* Decorative elements */}
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-green-200 rounded-full flex items-center justify-center">
                <svg
                  className="w-8 h-8 text-green-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={3}
                    d="M5 13l4 4L19 7"
                  />
                </svg>
              </div>
            </div>

            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold mb-4">
                {Translator.singular("Let's get started")}
              </h1>
              <p className="text-lg text-gray-600 max-w-xl mx-auto">
                {Translator.singular(
                  "Heja is a free team app for sports. Now, anyone can use it to fundraise. Here's how to get started:"
                )}
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
              {/* Step 1 */}
              <div className="bg-gray-100 p-6 rounded-lg">
                <div className="text-center mb-4">
                  <div className="w-10 h-10 mx-auto mb-0">
                    <img src="/images/fundraising-step1.svg" alt="Step 1" className="w-10 h-10" />
                  </div>
                  <h3 className="text-blue-500 text-lg font-medium mb-2">Step 1</h3>
                </div>
                <h4 className="text-xl font-bold mb-4">Download Heja</h4>
                <ul className="space-y-2 mb-6 text-gray-700 list-disc list-outside pl-5">
                  <li>Get the Heja app</li>
                  
                  <li>Set up your team</li>
                  <li>You&apos;ll land on the home screen, go to the fundraising tab</li>
                </ul>
                <div className="flex gap-2 border-t border-gray-200 pt-4">
                  <a
                    href="https://apps.apple.com/us/app/heja/id1157335714"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      // Track Meta pixel event directly
                      trackMetaPixel('Lead', {
                        content_name: 'app_store_download',
                        content_category: 'app_download',
                        source: 'fundraising_guide',
                      })

                      // Track via GTM (backup)
                      logEvent('Lead', {
                        content_name: 'app_store_download',
                        content_category: 'app_download',
                        source: 'fundraising_guide',
                      })

                      // Track analytics event
                      trackEvent('fundraising_app_store_clicked')
                    }}
                  >
                    <img
                      src={`/images/${lang}/download-app-store.svg`}
                      alt="Download on App Store"
                      className="h-10"
                    />
                  </a>
                  <a
                    href="https://play.google.com/store/apps/details?id=com.heja.app"
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      // Track Meta pixel event directly
                      trackMetaPixel('Lead', {
                        content_name: 'google_play_download',
                        content_category: 'app_download',
                        source: 'fundraising_guide',
                      })

                      // Track via GTM (backup)
                      logEvent('Lead', {
                        content_name: 'google_play_download',
                        content_category: 'app_download',
                        source: 'fundraising_guide',
                      })

                      // Track analytics event
                      trackEvent('fundraising_google_play_clicked')
                    }}
                  >
                    <img
                      src={`/images/${lang}/google-play-badge.png`}
                      alt="Get it on Google Play"
                      className="h-10"
                    />
                  </a>
                </div>
              </div>

              {/* Step 2 */}
              <div className="bg-gray-100 p-6 rounded-lg">
                <div className="text-center mb-4">
                  <div className="w-10 h-10 mx-auto mb-0">
                    <img src="/images/fundraising-step2.svg" alt="Step 2" className="w-10 h-10" />
                  </div>
                  <h3 className="text-blue-500 text-lg font-medium mb-2">Step 2</h3>
                </div>
                <h4 className="text-xl font-bold mb-4">Create your fundraiser</h4>
                <ul className="space-y-2 text-gray-700 list-disc list-outside pl-5">
                  <li>
                    Tap <strong>Fundraising</strong> on the bottom tab
                  </li>
                  <li>Hit &quot;+&quot; to start</li>
                  <li>Choose your product, set a goal, and schedule</li>
                </ul>
              </div>

              {/* Step 3 */}
              <div className="bg-gray-100 p-6 rounded-lg">
                <div className="text-center mb-4">
                  <div className="w-10 h-10 mx-auto mb-0">
                    <img src="/images/fundraising-step3.svg" alt="Step 3" className="w-10 h-10" />
                  </div>
                  <h3 className="text-blue-500 text-lg font-medium mb-2">Step 3</h3>
                </div>
                <h4 className="text-xl font-bold mb-4">Invite your team</h4>
                <ul className="space-y-2 text-gray-700 list-disc list-outside pl-5">
                  <li>
                    Go to <strong>Team tab</strong> and tap <strong>Invite team</strong>
                  </li>
                  <li>Share the team link and invite your team</li>
                  <li>
                    <span>
                      <strong>That&apos;s it, you&apos;re ready!</strong> 🚀
                    </span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="text-center flex items-center flex-col gap-2">
              <h3 className="text-xl font-bold mb-0">{Translator.singular('Need help?')}</h3>
              <p className="text-gray-600 mb-2">
                {Translator.singular("We're here if you need us.")}
              </p>
              <Button
                className="bg-blue-100 text-blue-500 hover:bg-blue-200 cursor-pointer"
                size="lg"
                onClick={() => intercom.show()}
              >
                {Translator.singular('Get support')}
              </Button>
            </div>
          </div>
        </main>
        <Footer />
      </>
    )
  }

  if (showForm) {
    return (
      <>
        <Navigation />
        <main className="bg-white min-h-screen py-12">
          <div className="max-w-md mx-auto px-6">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-bold mb-2">
                {Translator.singular('Get started with fundraising')}
              </h1>
              <p className="text-gray-600">
                {Translator.singular('Tell us about your fundraising goals')}
              </p>
            </div>

            <Form method="post" onSubmit={handleFormSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                  {Translator.singular('Email address')}
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={Translator.singular('your@email.com')}
                />
              </div>

              <div>
                <label htmlFor="cause" className="block text-sm font-medium text-gray-700 mb-2">
                  {Translator.singular('What are you fundraising for?')}
                </label>
                <input
                  type="text"
                  id="cause"
                  name="cause"
                  required
                  value={formData.cause}
                  onChange={(e) => setFormData({ ...formData, cause: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={Translator.singular(
                    'e.g., Team uniforms, tournament fees, equipment'
                  )}
                />
              </div>

              <div>
                <label htmlFor="goal" className="block text-sm font-medium text-gray-700 mb-2">
                  {Translator.singular('Fundraising goal')}
                </label>
                <input
                  type="text"
                  id="goal"
                  name="goal"
                  required
                  value={formData.goal}
                  onChange={(e) => setFormData({ ...formData, goal: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder={Translator.singular('e.g., $5,000')}
                />
              </div>

              <div className="flex gap-3">
                <Button
                  type="button"
                  size="lg"
                  className="flex-1 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300"
                  onClick={() => setShowForm(false)}
                >
                  {Translator.singular('Back')}
                </Button>
                <Button
                  type="submit"
                  size="lg"
                  className="flex-1 bg-black text-white rounded-md hover:bg-gray-800"
                >
                  {Translator.singular('Continue')}
                </Button>
              </div>
              <p className="text-gray-400 text-sm text-balance text-center">
                {Translator.singular('By continuing, you agree to recciving marketing emails from Heja')}
              </p>
            </Form>
          </div>
        </main>
        <Footer />
      </>
    )
  }

  return (
    <>
      <Navigation />
      <header className="bg-neutral-100 pt-12 md:pt-24">
        <div className="max-w-5xl mx-auto flex flex-col-reverse gap-6 md:flex-row items-center md:items-end text-left">
          <div className="lg:max-w-[45%] flex flex-1 pt-6">
            <img
              src="/images/fundraiser-hero.webp"
              role="presentation"
              width={500}
              height={500}
              alt="Heja sports team graphic"
              className="block m-auto max-w-[90%]"
            />
          </div>
          <div className="flex flex-1 flex-col gap-2 px-6 md:pb-16 mx-auto text-center md:text-left">
            <div className="text-white bg-blue-500 rounded-full px-2 py-1 text-xs self-center md:self-start font-medium">
              Currently US only
            </div>
            <h1 className="text-3xl md:text-5xl font-semibold max-w-[470px]">
              Fundraise with Heja in&nbsp;minutes
            </h1>
            <p className="md:text-lg text-balance md:pt-4 max-md:pb-2">
              {Translator.singular(
                'Raise team funds digitally – no fees or product handling. Keep a percentage of each sale.'
              )}
            </p>
            <div className="flex flex-row items-center max-md:justify-center gap-2">
              <img
                src="/images/icons/checkmark.svg"
                role="presentation"
                width={20}
                height={20}
                alt="checkmark"
              />
              <p className="md:text-lg">{Translator.singular('No fees, minimums or inventory')}</p>
            </div>
            <div className="flex flex-row items-center max-md:justify-center gap-2">
              <img
                src="/images/icons/checkmark.svg"
                role="presentation"
                width={20}
                height={20}
                alt="checkmark"
              />
              <p className="md:text-lg">
                {Translator.singular(`Product ships straight to buyer's door`)}
              </p>
            </div>
            <div className="flex flex-row items-center max-md:justify-center gap-2">
              <img
                src="/images/icons/checkmark.svg"
                role="presentation"
                width={20}
                height={20}
                alt="checkmark"
              />
              <p className="md:text-lg">{Translator.singular('Cancel any time')}</p>
            </div>
            <div className="flex flex-row gap-2 max-md:justify-center">
              <Button
                className="block bg-black mt-4 text-white font-normal rounded-md cursor-pointer"
                size="lg"
                onClick={handleGetStarted}
              >
                {Translator.singular('Get started')}
              </Button>
              <a
                href="https://heja.io/shop/example?utm_source=heja-fundraising-page"
                target="_blank"
                rel="noreferrer"
              >
                <Button
                  variant="outline"
                  className="block bg-transparent mt-4 text-black border-2 border-black font-normal cursor-pointer"
                  size="lg"
                >
                  {Translator.singular('Demo store')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </header>
      <main className="bg-white pb-32">
        <div className="bg-primary-color">
          <div className="max-w-5xl mx-auto py-20 px-6">
            <h3 className="text-white text-2xl text-center font-medium mb-10">
              {Translator.singular('Fundraising options')}
            </h3>
            <div className="flex flex-col max-lg:items-center md:grid md:grid-cols-2 lg:grid-cols-3 gap-6 text-white">
              <div className="max-w-[350px]">
                <img
                  src="/images/toto-cookies.jpg"
                  role="presentation"
                  width={350}
                  height={350}
                  alt=""
                />
                <h3 className="text-lg font-semibold mt-3">Toto Cookies</h3>
                <p className="text-sm opacity-90">
                  {Translator.singular(
                    'An ooey-gooey, drool-worthy delicious cookie made with pure superfood ingredients, powerful adaptogens, and no artificial anything.'
                  )}
                </p>
                <div className="mt-4 text-sm flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-2">
                    <img src="/images/flower-bullet.svg" width={16} height={16} alt="checkmark" />
                    <p>{Translator.singular('Each pack contains 10 cookies')}</p>
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    <img src="/images/flower-bullet.svg" width={16} height={16} alt="checkmark" />
                    <p>{Translator.singular('Gluten free')}</p>
                  </div>
                </div>
              </div>
              <div className="max-w-[350px]">
                <img
                  src="/images/citypop-popcorn.webp"
                  role="presentation"
                  width={350}
                  height={350}
                  alt=""
                />
                <h3 className="text-lg font-bold mt-3">City Pop Popcorn</h3>
                <p className="text-sm ">
                  {Translator.singular(
                    'Small-batch, crave-worthy popcorn in bold sweet and savory flavors. Made with high-quality, non-GMO kernels for the perfect crunch.'
                  )}
                </p>
                <div className="mt-4 text-sm flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-2">
                    <img src="/images/flower-bullet.svg" width={16} height={16} alt="checkmark" />
                    <p>{Translator.singular('Choose from 12 flavors')}</p>
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    <img src="/images/flower-bullet.svg" width={16} height={16} alt="checkmark" />
                    <p>{Translator.singular('Single and multi packs available')}</p>
                  </div>
                </div>
              </div>
              <div className="max-w-[350px]">
                <img
                  src="/images/twistedflavors-pretzels.webp"
                  role="presentation"
                  width={350}
                  height={350}
                  alt=""
                />
                <h3 className="text-lg font-bold mt-3">Twisted Flavors Pretzels</h3>
                <p className="text-sm ">
                  {Translator.singular(
                    'Twisted, crunchy pretzels packed with bold, addictive flavor. From sweet to savory, every crunchy twist is packed with addictive flavor.'
                  )}
                </p>
                <div className="mt-4 text-sm flex flex-col gap-2">
                  <div className="flex flex-row items-center gap-2">
                    <img src="/images/flower-bullet.svg" width={16} height={16} alt="checkmark" />
                    <p>{Translator.singular('Choose from 15 flavors')}</p>
                  </div>
                  <div className="flex flex-row items-center gap-2">
                    <img src="/images/flower-bullet.svg" width={16} height={16} alt="checkmark" />
                    <p>{Translator.singular('Single and multi packs available')}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="pb-4 py-16 md:py-32 px-4 md:px-8 max-w-5xl mx-auto relative">
          <h2 className="text-2xl mb-6">{Translator.singular(`Why it's so easy`)}</h2>
          <img
            src="/images/fundraiser-callout.svg"
            role="presentation"
            width={160}
            height={160}
            className="absolute top-4 md:top-24 right-0 md:right-[0%]"
            alt=""
          />
          <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:gap-8">
            <div className="flex flex-col p-3 md:pt-10 flex-1 bg-neutral-100 rounded-lg">
              <div className="h-10 w-10 mb-2 flex justify-center items-center">
                <img
                  src="/images/fundraiser-icon-1.svg"
                  role="presentation"
                  width={40}
                  height={40}
                  alt=""
                />
              </div>
              <p>
                <span className="font-medium">{Translator.singular('Get started:')}</span>{' '}
                {Translator.singular('Set a date and goal for your team’s pop-up webshop.')}
              </p>
            </div>
            <div className="flex flex-col p-3 md:pt-10 flex-1 bg-neutral-100 rounded-lg">
              <div className="h-10 w-10 mb-2 flex justify-center items-center">
                <img
                  src="/images/fundraiser-icon-2.svg"
                  role="presentation"
                  width={40}
                  height={40}
                  alt=""
                />
              </div>
              <p>
                <span className="font-medium">{Translator.singular('Share & sell:')}</span>{' '}
                {Translator.singular('Send the link to friends and family to start selling.')}
              </p>
            </div>
            <div className="flex flex-col p-3 md:pt-10 flex-1 bg-neutral-100 rounded-lg">
              <div className="h-10 w-10 mb-2 flex justify-center items-center">
                <img
                  src="/images/fundraiser-icon-3.svg"
                  role="presentation"
                  alt=""
                  width={40}
                  height={40}
                />
              </div>
              <p>
                <span className="font-medium">{Translator.singular('Track progress:')}</span>{' '}
                {Translator.singular('See your team’s success with live updates and leaderboards.')}
              </p>
            </div>
            <div className="flex flex-col p-3 md:pt-10 flex-1 bg-neutral-100 rounded-lg">
              <div className="h-10 w-10 mb-2 flex justify-center items-center">
                <img
                  src="/images/fundraiser-icon-4.svg"
                  role="presentation"
                  width={40}
                  height={40}
                  alt=""
                />
              </div>
              <p>
                <span className="font-medium">{Translator.singular('No fuss:')}</span>{' '}
                {Translator.singular(
                  'Orders ship directly to buyers - no inventory needed on your end.'
                )}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-primary-color">
          <div className="py-16 md:py-28 px-4 md:px-10 max-w-5xl mx-auto relative">
            <h2 className="text-2xl mb-8 text-white">{Translator.singular(`Success stories`)}</h2>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:gap-8">
              <div className="p-4 bg-neutral-100 rounded-lg">
                <div className="flex flex-row items-center gap-4 pb-4">
                  <div className="relative w-16 h-16">
                    <div className="rounded-full bg-pink-400 w-16 h-16 absolute top-0.5 left-0.5" />
                    <img
                      src="/images/prissy-divas@2x.png"
                      alt=""
                      className="rounded-full relative z-10"
                    />
                  </div>
                  <div>
                    <div className="flex flex-row items-center gap-2">
                      <h6 className="font-semibold">{Translator.singular('Coach Prissy')}</h6>

                      <img
                        src="/images/icons/checkmark-pink.svg"
                        role="presentation"
                        width={16}
                        height={16}
                        alt="checkmark"
                      />
                    </div>
                    <p>{Translator.singular('Coach')}</p>
                  </div>
                </div>
                <p>
                  {Translator.singular(
                    'Heja’s fundraiser was easy to set up and worked great for our {{ dancerCount }} dancers and supporters. It was a big success, and we’re so thankful Heja offers this feature.',
                    { dancerCount: HEJA_STATS.fundraisingDancerCount }
                  )}
                </p>
                <div className="flex flex-row items-center justify-between gap-2 mt-4">
                  <p className="font-semibold">{Translator.singular('Prissy Divas of New York')}</p>
                  <p className="bg-blue-500 text-white font-medium rounded-full px-4 py-0.5">
                    {Translator.singular('$6,445')}
                  </p>
                </div>
              </div>
              <div className="p-4 bg-neutral-100 rounded-lg">
                <div className="flex flex-row items-center gap-4 pb-4">
                  <div className="relative w-16 h-16">
                    <div className="rounded-full bg-pink-400 w-16 h-16 absolute top-0.5 left-0.5" />
                    <img
                      src="/images/luis-monjaras.png"
                      alt=""
                      className="rounded-full relative z-10"
                    />
                  </div>
                  <div>
                    <div className="flex flex-row items-center gap-2">
                      <h6 className="font-semibold">{Translator.singular('Luis Monjaras')}</h6>

                      <img
                        src="/images/icons/checkmark-pink.svg"
                        role="presentation"
                        width={16}
                        height={16}
                        alt="checkmark"
                      />
                    </div>
                    <p>{Translator.singular('Coach')}</p>
                  </div>
                </div>
                <p>
                  {Translator.singular(
                    'Heja fundraiser was easy and fast. Participants earned money for club needs - just by sharing a link. Everything else was handled for them.'
                  )}
                </p>
                <div className="flex flex-row items-center justify-between gap-2 mt-4">
                  <p className="font-semibold">{Translator.singular('Idaho Surf Hailey')}</p>
                  <p className="bg-blue-500 text-white font-medium rounded-full px-4 py-0.5">
                    {Translator.singular('$3,400')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="bg-neutral-100">
          <div className="max-w-5xl mx-auto py-12">
            <div className="max-w-3xl mx-auto relative p-12 flex flex-col items-center">
              <img
                src="/images/fundraiser-graphics-1.svg"
                role="presentation"
                width={80}
                height={40}
                alt=""
                className="absolute top-0 right-0"
              />
              <img
                src="/images/fundraiser-graphics-2.svg"
                role="presentation"
                width={80}
                height={40}
                alt=""
                className="absolute bottom-0 left-0"
              />
              <h2 className="text-3xl mb-2 text-center">
                {Translator.singular('Test out the pop-up shop')}
              </h2>
              <p className="text-lg font-normal text-center px-12 max-w-screen-sm mx-auto">
                {Translator.singular(
                  `See how easy it is for supporters to order online – no apps, logins, or hassle.`
                )}{' '}
              </p>
              <a
                href="https://heja.io/shop/example?utm_source=heja-fundraising-page"
                target="_blank"
                rel="noreferrer"
              >
                <Button size="lg" className="bg-black text-white mt-6 font-normal cursor-pointer">
                  {Translator.singular('Open the demo shop')}
                </Button>
              </a>
            </div>
          </div>
        </div>
        <div className="px-4 max-w-5xl mx-auto">
          <div className="flex flex-col md:flex-row gap-4 text-center md:text-left items-center md:items-center justify-between mt-4 lg:mt-8 rounded-md border-2 border-pink-400 p-4 md:px-8 md:py-12">
            <div className="flex flex-col gap-2">
              <h1 className="text-2xl font-bold">
                {Translator.singular('Start your effortless fundraising journey')}
              </h1>
              <p className="md:text-balance">
                {Translator.singular(
                  'Show your interest today and we’ll help you get your team’s fundraising up and running in no time. '
                )}
              </p>
            </div>
            <Button
              size="lg"
              className="bg-black text-white font-normal cursor-pointer"
              onClick={handleGetStarted}
            >
              {Translator.singular('Get started')}
            </Button>
          </div>
        </div>
      </main>

      <Footer />
    </>
  )
}
